import axios from 'axios';
import * as Sentry from '@sentry/react';
import API from '../axios';
import cleanString from './cleanString';
import { endUnauthorizedSession, isUnauthorizedResponse } from './unauthorizedSession';
import { isSedeMode } from './partnerAccess/partnerStorage';
import { canEndNormalSession } from './partnerAccess/sedeSession';

const TERMINAL_IMPORT_STATUSES = ['COMPLETED', 'COMPLETED_WITH_ERRORS'];
const TERMINAL_EXPORT_STATUSES = ['COMPLETED'];
const FAILED_STATUSES = ['FAILED', 'CANCELLED'];
const API_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
const OPERATION_KEY_PREFIX = 'contact-io:operation:';
const INCOMPLETE_RESPONSE_MESSAGE = 'El servicio de contactos respondió incompleto. Intenta nuevamente.';
const INSECURE_URL_MESSAGE = 'El servicio de contactos devolvió una dirección no segura.';

// The Bearer token, the contact file and the download all travel to these URLs: HTTPS only.
// Plain HTTP is accepted for localhost so the service can be run in development.
export const isSecureContactIoUrl = (value) => {
  try {
    const parsed = new URL(value);
    if (parsed.protocol === 'https:') return true;
    return parsed.protocol === 'http:' && ['localhost', '127.0.0.1'].indexOf(parsed.hostname) >= 0;
  } catch (error) {
    return false;
  }
};

export const normalizeContactIoRollout = (rollout) => {
  const apiBaseUrl = typeof rollout?.apiBaseUrl === 'string'
    ? rollout.apiBaseUrl.trim().replace(/\/$/, '')
    : '';

  return {
    enabled: rollout?.enabled === true && isSecureContactIoUrl(apiBaseUrl),
    apiBaseUrl,
  };
};

export const isContactIoRolloutEnabled = (rollout) => normalizeContactIoRollout(rollout).enabled;

// A failing probe drops every user back to the legacy flow, which looks exactly like "the rollout is
// off" — the kind of outage nobody notices. Reported once per page load so a backend outage does not
// turn every contacts screen into a Sentry event.
let rolloutFailureReported = false;

export const resetContactIoRolloutReporting = () => {
  rolloutFailureReported = false;
};

export const getContactIoRollout = async () => {
  // contact-io authenticates with the normal JWT and resolves the company from the user it names; it
  // does not understand the delegated token of partner (sede) mode. In a sede tab it would act on the
  // partner's own company, so the sede-aware legacy flow is used instead.
  if (isSedeMode()) return { enabled: false, apiBaseUrl: '' };
  try {
    const response = await API.get('/contact-io/rollout');
    return normalizeContactIoRollout(response.data);
  } catch (error) {
    // A 404 is not an outage: the dashboard and the backend deploy independently, so the endpoint is
    // simply not there yet. Reporting it would turn a normal deploy window into one Sentry event per
    // session.
    const isEndpointMissing = (error?.response?.status || error?.httpCode) === 404;
    if (!isEndpointMissing && !rolloutFailureReported) {
      rolloutFailureReported = true;
      Sentry.captureException(error, { tags: { component: 'contactIo', op: 'getContactIoRollout' } });
    }
    return { enabled: false, apiBaseUrl: '' };
  }
};

export const buildContactIoImportPayload = (formData) => {
  const file = formData.get('file');

  return {
    companyId: formData.get('companyId'),
    channelId: formData.get('channelId'),
    agentId: formData.get('agentId'),
    fileName: file?.name || 'contacts.csv',
    fileType: file?.type || 'text/csv',
    fileSize: file?.size || 0,
    idempotencyKey: getOperationKey(importFingerprint(formData), 'import'),
  };
};

// contact-io reads an empty channel list as "every channel the user can see", which would export
// contacts from other spaces. It is only used when the selected space names at least one active
// channel; `null` sends the export through the legacy flow and its space rules
// (`exportClientReportAction`).
export const getContactIoExportChannelIds = (selectedSpace) => {
  const channels = selectedSpace?.channels;
  if (!Array.isArray(channels)) return null;
  const ids = channels
    .filter((channel) => channel?.isActive === true && typeof channel.id === 'string' && channel.id)
    .map((channel) => channel.id);
  return ids.length > 0 ? ids : null;
};

export const buildContactIoExportPayload = ({
  format,
  filterContactsBy,
  companyId,
  initialDate,
  endDate,
  keyword,
  tagsIDS,
  agentID,
  channelIds,
  userId,
  isAdminOfIds = [],
  canSeePhoneNumber = false,
}) => {
  const cleanedKeyword = keyword ? cleanString(keyword) : null;
  const filters = {
    filterContactsBy,
    companyId,
    createdAtFrom: cleanedKeyword ? null : initialDate,
    createdAtTo: cleanedKeyword ? null : endDate,
    keyword: cleanedKeyword,
    tagsIds: Array.isArray(tagsIDS) ? tagsIDS : [],
    channelsIds: Array.isArray(channelIds) ? channelIds.filter(Boolean) : [],
    agentId: agentID === 'Todos' ? null : agentID,
    canSeePhoneNumber,
  };

  if (userId && isAdminOfIds?.length > 0) {
    filters.agentIdIn = isAdminOfIds;
  }

  return {
    companyId,
    format,
    filters,
    idempotencyKey: getOperationKey(`export:${format}:${JSON.stringify(filters)}`, `export-${format}`),
  };
};

export const importContactsWithContactIo = async (formData, rollout, setProgress) => {
  const normalizedRollout = normalizeContactIoRollout(rollout);
  if (!normalizedRollout.enabled) return null;

  const file = formData.get('file');
  const payload = buildContactIoImportPayload(formData);
  const client = createContactIoClient(normalizedRollout.apiBaseUrl);

  const createResponse = await client.post('/contact-imports', payload);
  if (!createResponse.data?.jobId || !createResponse.data?.upload?.url) throw new Error(INCOMPLETE_RESPONSE_MESSAGE);
  await uploadWithPresignedPost(createResponse.data.upload, file);
  if (setProgress) setProgress(15);

  await client.post(`/contact-imports/${createResponse.data.jobId}/complete-upload`);
  const completedJob = await pollContactIoJob({
    client,
    path: `/contact-imports/${createResponse.data.jobId}`,
    terminalStatuses: TERMINAL_IMPORT_STATUSES,
    onProgress: setProgress,
    operationKey: payload.idempotencyKey,
  });

  // A finished import without its result would render as "nothing happened"; treat it as a service error.
  if (!completedJob.result || !Array.isArray(completedJob.result.finalStatus)) {
    throw new Error(INCOMPLETE_RESPONSE_MESSAGE);
  }
  releaseOperationKey(payload.idempotencyKey);
  return completedJob.result;
};

export const exportContactsWithContactIo = async ({ rollout, payload, onProgress }) => {
  const normalizedRollout = normalizeContactIoRollout(rollout);
  if (!normalizedRollout.enabled) return false;

  const client = createContactIoClient(normalizedRollout.apiBaseUrl);
  const createResponse = await client.post('/contact-exports', payload);
  if (!createResponse.data?.jobId) throw new Error(INCOMPLETE_RESPONSE_MESSAGE);
  const completedJob = await pollContactIoJob({
    client,
    path: `/contact-exports/${createResponse.data.jobId}`,
    terminalStatuses: TERMINAL_EXPORT_STATUSES,
    onProgress,
    operationKey: payload.idempotencyKey,
  });

  if (!completedJob.downloadUrl) throw new Error(INCOMPLETE_RESPONSE_MESSAGE);
  downloadFromUrl(completedJob.downloadUrl);
  releaseOperationKey(payload.idempotencyKey);
  return true;
};

export const extractContactIoErrorMessage = (error, fallbackMessage = 'Ocurrió un error en el servicio de contactos. Intenta nuevamente.') => {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData.trim();
  if (responseData?.userMessage) return responseData.userMessage;
  if (responseData?.message) return responseData.message;
  if (responseData?.lastError) return responseData.lastError;
  if (responseData?.error) return responseData.error;
  if (error?.userMessage) return error.userMessage;
  if (error?.isAxiosError && !error?.response) return fallbackMessage;
  if (error?.message) return error.message;
  return fallbackMessage;
};

export const createContactIoClient = (apiBaseUrl) => {
  const client = axios.create({
    baseURL: apiBaseUrl,
    timeout: API_TIMEOUT_MS,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  });

  // contact-io verifies the same Bearer token the rest of the dashboard uses. A rejected token must
  // end the session here exactly as it does on the other clients; otherwise the import screen only
  // shows a toast and the user keeps browsing with a token the server no longer accepts. The error
  // is re-thrown untouched so extractContactIoErrorMessage still reads the service's own payload.
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Never while the tab is (or is reloading out of) a sede: that would wipe the normal session.
      if (isUnauthorizedResponse(error) && canEndNormalSession()) endUnauthorizedSession();
      return Promise.reject(error);
    },
  );

  return client;
};

const uploadWithPresignedPost = async (upload, file) => {
  if (!isSecureContactIoUrl(upload.url)) throw new Error(INSECURE_URL_MESSAGE);
  const uploadFormData = new FormData();
  Object.keys(upload.fields || {}).forEach((key) => {
    uploadFormData.append(key, upload.fields[key]);
  });
  uploadFormData.append('file', file);
  await axios.post(upload.url, uploadFormData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  });
};

const pollContactIoJob = async ({ client, path, terminalStatuses, onProgress, operationKey }) => {
  for (let attempt = 0; attempt < 240; attempt += 1) {
    const response = await client.get(path);
    const job = response.data;

    if (onProgress && typeof job.progress === 'number') {
      onProgress(job.progress);
    }

    // The key is released by the caller once the terminal payload is validated: if the result is
    // incomplete, the retry must reuse the same key to get the same job back.
    if (terminalStatuses.indexOf(job.status) >= 0) return job;
    if (FAILED_STATUSES.indexOf(job.status) >= 0) {
      releaseOperationKey(operationKey);
      throw new Error(job.lastError || 'Ocurrió un error en el servicio de contactos. Intenta nuevamente.');
    }

    await delay(2500);
  }

  throw new Error('La tarea está tardando más de lo esperado. Intenta revisar el resultado en unos minutos.');
};

const downloadFromUrl = (url) => {
  // A javascript: or data: href would execute in this origin; only a signed HTTPS URL is a download.
  if (!isSecureContactIoUrl(url)) throw new Error(INSECURE_URL_MESSAGE);
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const delay = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

const createIdempotencyKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Every field that changes what the import DOES has to be in here, because the key derived from it
// is kept in sessionStorage and reused. `agentId` decides who the contacts are assigned to: without
// it, importing the same file again for a different agent hits the same key, the server answers with
// the original job, and the contacts keep the first agent's assignment.
const importFingerprint = (formData) => {
  const file = formData.get('file');
  return [
    'import',
    formData.get('companyId'),
    formData.get('channelId'),
    formData.get('agentId'),
    file?.name,
    file?.size,
    file?.lastModified,
  ].join(':');
};

// One key per user operation: a retry after a lost response reuses it, so the server returns the
// same job instead of importing the file twice. The key is released once the job reaches a final state.
const getOperationKey = (fingerprint, prefix) => {
  const storageKey = OPERATION_KEY_PREFIX + fingerprint;
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const created = createIdempotencyKey(prefix);
    sessionStorage.setItem(storageKey, created);
    return created;
  } catch (error) {
    return createIdempotencyKey(prefix);
  }
};

const releaseOperationKey = (operationKey) => {
  if (!operationKey) return;
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.indexOf(OPERATION_KEY_PREFIX) === 0 && sessionStorage.getItem(key) === operationKey)
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    // Storage unavailable: nothing was stored, nothing to release.
  }
};
